import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import type { MapData } from '@shared/types/map';
import type { PlayerStateView } from '../../state/GameStateView';
import { TILE_SIZE, ASSETS_BASE_URL } from '@shared/constants';
import { Direction } from '@shared/types/entity';
import { ClientMessageType } from '@shared/types/messages';
import { buildWaterIndex } from '@shared/utils/waterUtils';
import { MapOverlay } from '../ui/MapOverlay';
import { Minimap } from '../ui/Minimap';
import { getSurfaceTextureKey, getSurfaceById } from '../../sprites/SurfaceSpriteManager';
import { SURFACE_FOOTSTEP_SOUNDS, WATER_FOOTSTEP_SOUND } from '@shared/data/sounds';
import { playAnimationAt } from '../../sprites/AnimationSpriteManager';
import {
  WATER_FRAME_MS,
  getWaterFrameCount,
  getWaterFrameTextureKey,
} from '../../sprites/WaterSpriteManager';
import { getDecorationAssetUrl, isDecorationKey } from '../../util/decorationAssets';

/**
 * Carga y montaje del mapa: precarga los atlases/texturas que el mapa necesita (preloadMapAssets),
 * arma el mundo desde el MapData (loadAndCreateMap → índice de tiles, culling inicial, agua,
 * translados, luces), arranca el timer de animación de agua y registra sprites de translado y luces.
 * Extraído de GameScene (god-object). Delega el render de tiles a `this.scene.mapTiles.*` y las
 * partículas a `this.scene.particleFx.*`. Ver docs/MAP_FORMAT.md, docs/WORLD_BUILDER.md.
 */
export class MapLoader {
  constructor(private scene: GameScene) {}

  public preloadMapAssets(mapData: MapData): void {
    const seen = new Set<string>();

    const loadKey = (key: string | undefined): void => {
      if (!key || seen.has(key)) return;
      seen.add(key);
      // Árboles / decoraciones del usuario: tree1..N (nuevos) y decoration1..N (legacy)
      if (isDecorationKey(key)) {
        this.scene.load.image(key, getDecorationAssetUrl(key));
      } else if (key.startsWith('item_icon_')) {
        // Iconos de items: item_icon_noicon → /assets/item/noicon.png
        const itemFile = key.replace('item_icon_', '');
        this.scene.load.image(key, `${ASSETS_BASE_URL}/item/${itemFile}.png`);
      }
      // Superficies indexadas se cargan desde atlas en setupSurfaceTextures().
    };

    for (const layer of mapData.layers) {
      // Saltar capas internas (no necesitan assets)

      if (layer.name.startsWith('_')) continue;

      for (const tile of layer.tiles) {
        loadKey(tile.sprite);
      }
    }

    // Sprites de translados: pueden referenciar decoraciones que no están en
    // ninguna capa. Sin esto, el sprite no se carga y el marker queda invisible.
    if (mapData.translados) {
      for (const t of mapData.translados) {
        loadKey(t.sprite);
      }
    }
  }

  public loadAndCreateMap(mapData: MapData): void {
    this.scene.currentMap = mapData;

    // Minimapa in-game: crearlo una vez. El bake del terreno (sprites reales) se hace
    // recién cuando las texturas/superficies están cargadas (ver create()), NO acá:
    // loadAndCreateMap puede correr antes de setupSurfaceTextures y hornearía colores
    // (y envenenaría la textura compartida con el world-map overlay).
    if (!this.scene.minimap) {
      this.scene.minimap = new Minimap(this.scene, { onOpenWorldMap: () => this.scene.mapOverlay?.toggle() });
    }

    // Instanciar el world map overlay (tecla M). Si ya existía (recarga de mapa
    // a futuro multi-map), recrearlo para que use el mapa actual.
    if (this.scene.mapOverlay) { this.scene.mapOverlay.destroy(); this.scene.mapOverlay = null; }
    this.scene.mapOverlay = new MapOverlay({
      scene: this.scene,
      mapData,
      getLocalPlayerTile: () => {
        if (!this.scene.localSessionId) return null;
        const render = this.scene.playerObjects.get(this.scene.localSessionId);
        if (!render) return null;
        return {
          x: Math.floor(render.currentWorldX / TILE_SIZE),
          y: Math.floor(render.currentWorldY / TILE_SIZE),
        };
      },
      getLocalPlayerVisual: () => {
        if (!this.scene.localSessionId || !this.scene.room) return null;
        const ps = this.scene.room.state.players.get(this.scene.localSessionId) as PlayerStateView | undefined;
        if (!ps) return null;
        // bodyId = armadura si está equipada, sino nakedBodyId.
        const effBody = (ps.bodyId && ps.bodyId > 0) ? ps.bodyId : (ps.nakedBodyId ?? 1);
        return {
          bodyId: effBody,
          headId: ps.headId ?? 0,
          helmId: (ps as PlayerStateView & { helmId?: number }).helmId ?? 0,
          weaponId: ps.weaponId ?? 0,
          shieldId: ps.shieldId ?? 0,
          bootId: (ps as PlayerStateView & { bootId?: number }).bootId ?? 0,
          direction: (ps.direction ?? Direction.DOWN) as Direction,
        };
      },
      getCharacterId: () => this.scene.configData?.characterId ?? null,
      // Admin: click derecho sobre un tile del mapa abierto → teleport ahí.
      isAdmin: () => this.scene.isLocalAdmin(),
      onAdminTeleport: (tileX, tileY) => {
        this.scene.colyseusClient?.send({
          type: ClientMessageType.ADMIN_TELEPORT,
          x: tileX,
          y: tileY,
        });
      },
      // World Map interactivo: quests activas + grillas de colisión para rutas.
      getActiveQuests: () => this.scene.questUI?.getActiveQuests() ?? [],
      // Catálogo de quests del dock: completadas (para distinguir hechas) + nivel del player
      // (para gatear por color). Ver docs/WORLD_MAP.md.
      getCompletedQuestIds: () => this.scene.questUI?.getCompletedQuestIds() ?? new Set<string>(),
      getPlayerLevel: () => this.scene.getLocalPlayerLevel(),
      getBlockedTiles: () => this.scene.blockedTiles,
      getWaterTiles: () => this.scene.waterTiles,
      // Ruta de quest: fuente única = quest favorita de QuestUI (estrella del panel
      // flotante). Al abrir el mapa arranca en esa; trackear desde el dock la sincroniza.
      getTrackedQuestId: () => this.scene.questUI?.getFavoritedQuestId() ?? null,
      onTrackQuestChange: (questId) => this.scene.questUI?.setFavoritedQuest(questId),
      // Ocultar el panel flotante de quests mientras el mapa está abierto (evita solaparse).
      onVisibilityChange: (visible) => this.scene.questUI?.setMapOpen(visible),
    });

    // Verificar si todo está listo para ocultar la pantalla de carga
    this.scene.loadingScreenUi.checkAndHideLoadingScreen();

    // =====================================================================
    // NO USAR CONTAINER - Phaser solo ordena por depth cuando los sprites
    // están directamente en la escena. Los containers ignoran el depth de sus hijos.
    // =====================================================================
    this.scene.mapContainer = null;
    
    // AO-like: Conectar el sistema de grilla (sin container)
    this.scene.mapDebugGridSystem?.setMapContainer(null);

    // Indexar tiles bloqueados para debug de grilla / navegación
    this.scene.blockedTiles = new Set<string>();

    // Índice liviano de sonido de paso por superficie: sólo tiles cuyo suelo mapea a un sonido
    // NO-default (ej. caminos/tierra/dungeon). El pasto (default) NO se indexa (mayoría del mapa).
    // Se resuelve por la superficie de piso MÁS DE ARRIBA que se pisa: los senderos/tierra viven en
    // `layer2_floor_objects` pintados sobre el `layer1_ground`, así que hay que mirar ambas capas y
    // dejar que la superior gane. Ver docs/SOUND_AND_MUSIC.md.
    this.scene.footstepSurfaceSounds = new Map<string, string>();
    const grassFootstep = SURFACE_FOOTSTEP_SOUNDS['grass'];
    const fsSounds = this.scene.footstepSurfaceSounds;

    for (const layer of mapData.layers) {
      if (layer.name.startsWith('_')) continue;

      // Capas de piso "pisables" (ground + floor_objects). Se recorren en el orden del array
      // (ground primero, floor_objects después), así el override "la de arriba gana" sale gratis en
      // una sola pasada. `ground_deco_over`/`roof` no tienen superficies → no aportan al sonido.
      const isFloorLayer =
        layer.name === 'layer1_ground' || layer.name === 'layer2_floor_objects';

      for (const tile of layer.tiles) {
        if (tile.blocked) {
          this.scene.blockedTiles.add(`${tile.x},${tile.y}`);
        }
        if (!isFloorLayer) continue;

        // Resolver por SUBTYPE (grass/dirt/path/floor/house/dungeon/biomas…); fallback al type.
        const surf = getSurfaceById(tile.sprite);
        if (!surf) continue;
        const key = surf.subtype ?? surf.type;
        const tileKey = `${tile.x},${tile.y}`;
        // El agua la maneja `waterTiles`/costa (resolveFootstepSound la chequea ANTES): nunca se
        // indexa (evita ~843k entradas inútiles) y, si una capa superior pinta agua, limpia lo de abajo.
        if (key === 'water') {
          fsSounds.delete(tileKey);
          continue;
        }
        const sound = (key && SURFACE_FOOTSTEP_SOUNDS[key]) || grassFootstep;
        // Pasto (default) no se indexa (fallback en resolveFootstepSound); además, pasto pintado
        // ARRIBA de un piso no-pasto lo revierte a default.
        if (sound === grassFootstep) fsSounds.delete(tileKey);
        else fsSounds.set(tileKey, sound);
      }
    }

    // Precargar los sonidos de paso (con sus variantes) para que la variedad se oiga desde el primer
    // paso, sin decode async en el primer uso de cada variante. Ver docs/SOUND_AND_MUSIC.md.
    this.scene.audio.preload([...new Set(Object.values(SURFACE_FOOTSTEP_SOUNDS)), WATER_FOOTSTEP_SOUND]);

    this.scene.mapDebugGridSystem?.setBlockedTiles(this.scene.blockedTiles ?? new Set<string>());

    // Indexar agua NAVEGABLE con el mismo helper shared que usa el server
    // (MapSystem.waterIndex) → la predicción local espeja la regla exacta.
    // Ver shared/utils/waterUtils.ts y docs/WATER_AND_BOATS.md.
    this.scene.waterTiles = buildWaterIndex(mapData);

    // Construir el índice de entradas de tile + buckets por chunk para el
    // viewport culling. NO se crean GameObjects todavía: updateMapCulling()
    // instancia sólo los chunks visibles.
    this.scene.mapTiles.buildMapTileIndex(mapData);
    this.scene.mapTiles.updateMapCulling(true);

    // Agua animada: arrancar el timer global de frames (idempotente).
    this.ensureWaterAnimTimer();

    // Register map lights from the lights array
    this.registerMapLights(mapData);

    // Partículas ambiente del mapa (sectores custom).
    this.scene.particleFx.registerMapParticles(mapData);

    // Render translado sprites (gráfico opcional sobre el tile de cada translado)
    this.renderTransladoSprites(mapData);

    // Objetos de decoración con luz acoplada / animación (fogatas, etc.).
    this.renderDecoObjects(mapData);
  }

  /**
   * Renderiza los `decoObjects` del mapa: sprite estático o animación en LOOP, más su luz acoplada
   * opcional. Cosmético cliente. Ver docs/MAP_FORMAT.md y shared MapDecoObjectData.
   */
  public renderDecoObjects(mapData: MapData): void {
    // Teardown de una carga previa (idempotente).
    for (const s of this.scene.decoObjectSprites.values()) s.destroy();
    this.scene.decoObjectSprites.clear();
    for (const id of this.scene.decoObjectLightIds) this.scene.lightingSystem?.removeLightSource(id);
    this.scene.decoObjectLightIds.clear();

    const decos = mapData.decoObjects;
    if (!decos || decos.length === 0) return;

    for (const deco of decos) {
      const worldX = deco.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = deco.y * TILE_SIZE + TILE_SIZE / 2 + (deco.offsetY ?? 0);
      // Depth como decoración sobre el suelo (por debajo de entidades en la misma Y).
      const depth = this.scene.getEntityBaseDepthFromTileY(deco.y) - 0.5;

      if (typeof deco.animationId === 'number') {
        const sprite = playAnimationAt(this.scene, deco.animationId, worldX, worldY, true, depth);
        if (sprite) this.scene.decoObjectSprites.set(deco.id, sprite);
      } else if (deco.sprite) {
        const texKey = getSurfaceTextureKey(deco.sprite);
        if (this.scene.textures.exists(texKey)) {
          const img = this.scene.add.image(worldX, worldY, texKey).setDepth(depth);
          this.scene.decoObjectSprites.set(deco.id, img);
        }
      }

      // Luz acoplada opcional.
      if (deco.light && this.scene.lightingSystem) {
        const colorValue =
          typeof deco.light.color === 'string'
            ? parseInt(deco.light.color.replace('0x', ''), 16)
            : deco.light.color;
        const lightId = `deco_${deco.id}`;
        this.scene.lightingSystem.addLightSource(lightId, {
          x: worldX,
          y: deco.y * TILE_SIZE + TILE_SIZE / 2,
          radius: deco.light.radius,
          intensity: deco.light.intensity,
          color: colorValue,
          flicker: deco.light.flicker ?? false,
        });
        this.scene.decoObjectLightIds.add(lightId);
      }
    }
  }

  /**
   * Arranca (una sola vez) el timer que cicla los frames del agua. Índice de
   * frame GLOBAL → todos los bloques de agua visibles en fase (océano coherente).
   */
  public ensureWaterAnimTimer(): void {
    if (this.scene.waterAnimTimer) return;
    this.scene.waterAnimTimer = this.scene.time.addEvent({
      delay: WATER_FRAME_MS,
      loop: true,
      callback: () => {
        this.scene.waterFrame++;
        for (const { sprite, type } of this.scene.waterSprites.values()) {
          sprite.setTexture(getWaterFrameTextureKey(type, this.scene.waterFrame % getWaterFrameCount(type)));
        }
      },
    });
  }

  /**
   * Renderiza el gráfico opcional configurado para cada translado del mapa.
   * Si translado.sprite no está set, no se dibuja nada (el translado funciona
   * igual, sin marcador visual en gameplay).
   */
  public renderTransladoSprites(mapData: MapData): void {
    if (!mapData.translados) return;
    for (const t of mapData.translados) {
      if (!t.sprite) continue;
      // Resolver textura: las surfaces usan key generada
      let textureKey = t.sprite;
      const surfaceTextureKey = getSurfaceTextureKey(t.sprite);
      if (this.scene.textures.exists(surfaceTextureKey)) {
        textureKey = surfaceTextureKey;
      }
      if (!this.scene.textures.exists(textureKey)) {
        console.warn(`[GameScene] Textura no encontrada para translado sprite: ${t.sprite}`);
        continue;
      }
      const worldX = t.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = t.y * TILE_SIZE + TILE_SIZE / 2;
      const sprite = this.scene.add.image(worldX, worldY, textureKey);
      sprite.setOrigin(0.5, 0.5);
      // Depth como ground_deco_under: encima del suelo, debajo del personaje
      sprite.setDepth(2);
    }
  }

  /**
   * Registers static lights defined in the map data.
   * These are lights placed at specific tile positions (torches, campfires, etc.)
   */
  public registerMapLights(mapData: MapData): void {
    if (!mapData.lights || !this.scene.lightingSystem) return;
    
    const TILE_SIZE = 32;
    
    for (const light of mapData.lights) {
      // Convert tile coordinates to world coordinates (center of tile)
      const worldX = light.x * TILE_SIZE + TILE_SIZE / 2;
      const worldY = light.y * TILE_SIZE + TILE_SIZE / 2;
      
      // Parse color from hex string (e.g., "0xFFAA44") to number
      const colorValue = typeof light.color === 'string'
        ? parseInt(light.color.replace('0x', ''), 16)
        : light.color;
      
      const lightId = `map_${light.id}`;

      // `dark:true` → fuente de OSCURIDAD (sombra suave) en vez de luz.
      if (light.dark) {
        this.scene.lightingSystem.addDarkSource(lightId, {
          x: worldX,
          y: worldY,
          radius: light.radius,
          intensity: light.intensity,
          color: colorValue,
        });
        continue;
      }

      this.scene.lightingSystem.addLightSource(lightId, {
        x: worldX,
        y: worldY,
        radius: light.radius,
        intensity: light.intensity,
        color: colorValue,
        flicker: light.flicker ?? false,
      });
    }
  }

}
